"use server";

import { headers } from "next/headers";
import { redirect } from "next/navigation";
import { z } from "zod";
import { emailSchema, errorState, isNextNavigation, parseForm, passwordSchema, type ActionState } from "@/lib/actions";
import { logActivity } from "@/lib/audit";
import { auth, emailVerificationRequired } from "@/lib/auth";
import { clientIp, limitAction, RateLimitError } from "@/lib/rate-limit";
import { getSession, safeNext } from "@/lib/session";

const appUrl = process.env.APP_URL ?? "http://localhost:3000";

// ---------- Masuk ----------
const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Kata sandi wajib diisi."),
  next: z.string().optional(),
});

export async function signInAction(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const parsed = parseForm(signInSchema, formData);
  if (!parsed.success) return parsed.state;
  const { email, password, next } = parsed.data;
  const target = safeNext(next);
  try {
    await limitAction("sign-in", { window: 15 * 60, max: 10 }, email);
    // callbackURL dipakai juga untuk email verifikasi yang dikirim ulang bila email belum terverifikasi.
    const res = await auth.api.signInEmail({
      body: { email, password, callbackURL: target ?? "/akun/lanjut" },
      headers: await headers(),
    });
    if ("twoFactorRedirect" in res && res.twoFactorRedirect) {
      redirect(`/masuk/verifikasi${target ? `?next=${encodeURIComponent(target)}` : ""}`);
    }
  } catch (error) {
    if (isNextNavigation(error)) throw error;
    return errorState(error, formData);
  }
  redirect(target ?? "/akun/lanjut");
}

// ---------- Verifikasi dua langkah saat masuk ----------
const twoFactorSchema = z.object({
  code: z.string().trim().min(6, "Masukkan kode.").max(32),
  mode: z.enum(["totp", "backup"]).default("totp"),
  next: z.string().optional(),
});

export async function verifyTwoFactorAction(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const parsed = parseForm(twoFactorSchema, formData);
  if (!parsed.success) return parsed.state;
  const { code, mode, next } = parsed.data;
  try {
    await limitAction("two-factor", { window: 5 * 60, max: 10 });
    const h = await headers();
    if (mode === "backup") await auth.api.verifyBackupCode({ body: { code }, headers: h });
    else await auth.api.verifyTOTP({ body: { code: code.replace(/\s/g, "") }, headers: h });
  } catch (error) {
    return errorState(error, formData);
  }
  redirect(safeNext(next) ?? "/akun/lanjut");
}

// ---------- Daftar ----------
const signUpSchema = z
  .object({
    name: z.string().trim().min(3, "Nama minimal 3 karakter.").max(100),
    email: emailSchema,
    password: passwordSchema,
    confirmPassword: z.string(),
    next: z.string().optional(),
  })
  .refine((v) => v.password === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "Konfirmasi kata sandi tidak sama.",
  });

export async function signUpAction(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const parsed = parseForm(signUpSchema, formData);
  if (!parsed.success) return parsed.state;
  const { name, email, password, next } = parsed.data;
  try {
    // 30/jam per IP: kantor dengan satu IP publik tetap bisa mendaftarkan beberapa staf; akun tetap wajib verifikasi email.
    await limitAction("sign-up", { window: 60 * 60, max: 30 });
    await auth.api.signUpEmail({
      body: { name, email, password, callbackURL: safeNext(next) ?? "/akun/lanjut" },
      headers: await headers(),
    });
  } catch (error) {
    return errorState(error, formData);
  }
  // Tanpa verifikasi (dev), Better Auth langsung membuat sesi saat daftar.
  if (!emailVerificationRequired) redirect(safeNext(next) ?? "/akun/lanjut");
  redirect(`/daftar/cek-email?email=${encodeURIComponent(email)}`);
}

const resendSchema = z.object({ email: emailSchema });

export async function resendVerificationAction(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const parsed = parseForm(resendSchema, formData);
  if (!parsed.success) return parsed.state;
  try {
    await limitAction("resend-verification", { window: 15 * 60, max: 3 }, parsed.data.email);
    await auth.api.sendVerificationEmail({ body: { email: parsed.data.email, callbackURL: "/akun/lanjut" } });
  } catch (error) {
    // Error lain sengaja diabaikan: jangan bocorkan apakah email terdaftar/sudah terverifikasi.
    if (error instanceof RateLimitError) return errorState(error);
  }
  return { message: "Bila email terdaftar dan belum terverifikasi, tautan baru sudah dikirim." };
}

// ---------- Lupa & atur ulang kata sandi ----------
export async function forgotPasswordAction(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const parsed = parseForm(resendSchema, formData);
  if (!parsed.success) return parsed.state;
  try {
    await limitAction("forgot-password", { window: 15 * 60, max: 5 }, parsed.data.email);
    await auth.api.requestPasswordReset({
      body: { email: parsed.data.email, redirectTo: `${appUrl}/atur-ulang-kata-sandi` },
    });
  } catch (error) {
    if (error instanceof RateLimitError) return errorState(error);
  }
  return {
    ok: true,
    message: "Bila email terdaftar, tautan untuk mengatur kata sandi sudah dikirim. Periksa juga folder spam.",
  };
}

const resetSchema = z
  .object({ token: z.string().min(1), password: passwordSchema, confirmPassword: z.string() })
  .refine((v) => v.password === v.confirmPassword, {
    path: ["confirmPassword"],
    message: "Konfirmasi kata sandi tidak sama.",
  });

export async function resetPasswordAction(_prev: ActionState, formData: FormData): Promise<ActionState> {
  const parsed = parseForm(resetSchema, formData);
  if (!parsed.success) return parsed.state;
  try {
    await limitAction("reset-password", { window: 15 * 60, max: 10 });
    await auth.api.resetPassword({ body: { newPassword: parsed.data.password, token: parsed.data.token } });
  } catch (error) {
    return errorState(error);
  }
  redirect("/masuk?pesan=kata-sandi-diatur");
}

// ---------- Keluar ----------
export async function signOutAction() {
  const session = await getSession();
  await auth.api.signOut({ headers: await headers() });
  if (session) {
    await logActivity({
      event: "logout",
      actorId: session.user.id,
      subjectType: "user",
      subjectId: session.user.id,
      ip: await clientIp(),
    });
  }
  redirect("/masuk");
}
